import * as React from "react"

interface UseClipboardOptions {
  /** Duration in ms to show copied state (default: 2000) */
  timeout?: number
}

interface UseClipboardReturn {
  /** Whether the value was recently copied */
  copied: boolean
  /** Copy text to clipboard */
  copy: (text: string) => Promise<void>
  /** Error if copy failed */
  error: Error | null
}

/**
 * Hook to copy text to clipboard
 * @param options - Configuration options
 * @returns { copied, copy, error }
 */
export function useClipboard(options: UseClipboardOptions = {}): UseClipboardReturn {
  const { timeout = 2000 } = options
  const [copied, setCopied] = React.useState(false)
  const [error, setError] = React.useState<Error | null>(null)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const copy = React.useCallback(
    async (text: string) => {
      if (!navigator?.clipboard) {
        setError(new Error("Clipboard API not supported"))
        return
      }

      try {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setError(null)

        // Clear previous timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        // Reset copied state after timeout
        timeoutRef.current = setTimeout(() => {
          setCopied(false)
        }, timeout)
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to copy"))
        setCopied(false)
      }
    },
    [timeout]
  )

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return { copied, copy, error }
}
